import useToast from '@/hooks/use-toast.hook';
import { patchOrg } from '@/lib/services/org.service';
import { Box, Stack, Theme } from '@mui/material';
import { FC, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import ImageUploader from '@/components/image-uploader';
import ImageUploadPreviewCard from '@/components/image-uploader/preview-card';
import { ImageExtension } from '@/components/image-uploader/helpers';
import { OrgModel } from '@/lib/models/org/org.model';
import { OrgBrandingTheme } from '@/lib/models/org';
import BrandingThemeTabs from './branding-theme-tabs';
import { createUseStyles } from 'react-jss';

export type FileWithPreview = FileWithPath & { preview: string };

interface Props {
  organization: OrgModel;
  readonly: boolean;
  onUpload?: () => void;
  onRemove?: () => void;
}

const MAX_FILE_SIZE_KB = 300;
const IMAGE_HEIGHT_PX = 40;
const IMAGE_WIDTH_PX = 40;
const ALLOWED_IMAGE_TYPES: ImageExtension[] = ['png', 'jpg'];

const useStyles = createUseStyles<string, { mode: OrgBrandingTheme }>((theme: Theme) => ({
  uploadPreviewCard: {
    '& .MuiPaper-root': {
      backgroundColor: ({ mode }) =>
        mode === 'dark'
          ? theme.palette.portal.background.dark
          : theme.palette.portal.background.light,
    },
    '& .MuiPaper-root, & .MuiChip-label': {
      color: ({ mode }) =>
        mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    },
  },
}));

const PortalBadgeForm: FC<Props> = ({ organization, readonly, onUpload, onRemove }) => {
  const { portalBadge = { darkImage: '', image: '', title: 'Assistant' } } = organization.branding;

  const { toast, errorToast } = useToast();
  const [image, setImage] = useState<{ light: string; dark: string }>({
    light: portalBadge.image,
    dark: portalBadge.darkImage,
  });
  const [mode, setMode] = useState<OrgBrandingTheme>('light');
  const isDarkMode = mode === 'dark';

  const styles = useStyles({ mode });

  const handleUploadImage = (dataURL: string) => {
    const updatedOrg = new OrgModel(organization._props);

    if (isDarkMode) {
      updatedOrg.branding.portalBadge.darkImage = dataURL;
    } else {
      updatedOrg.branding.portalBadge.image = dataURL;
    }

    patchOrg(updatedOrg).then(({ patched, error }) => {
      if (patched) {
        onUpload?.();
        setImage((old) => {
          return {
            ...old,
            [mode]: dataURL,
          };
        });
        toast(`The ${mode} theme badge image was updated`);
        return;
      }

      errorToast(error || `The ${mode} theme badge image was not updated`);
    });
  };

  const handleRemoveImage = () => {
    const updatedOrg = new OrgModel(organization._props);

    if (isDarkMode) {
      updatedOrg.branding.portalBadge.darkImage = '';
    } else {
      updatedOrg.branding.portalBadge.image = '';
    }

    patchOrg(updatedOrg).then(({ patched, error }) => {
      if (patched) {
        onRemove?.();
        setImage((old) => {
          return {
            ...old,
            [mode]: '',
          };
        });
        toast(`The ${mode} theme badge image was removed`);
        return;
      }

      errorToast(error || `The ${mode} theme badge image was not removed`);
    });
  };

  const handleChangeMode = (tabPath: string) => {
    setMode(tabPath as OrgBrandingTheme);
  };

  return (
    <>
      <Box mb={4}>
        The Portal Assistant Badge appears next to assistant responses in the Portal, along with the
        assistant's title.
      </Box>
      <BrandingThemeTabs onChange={handleChangeMode} />
      <Stack direction="row" gap={6} width="100%">
        {!readonly && (
          <Box width="50%">
            <ImageUploader
              title="Upload New Badge"
              onUpload={handleUploadImage}
              maxFileSizeKb={MAX_FILE_SIZE_KB}
            />
          </Box>
        )}
        <Box width="50%">
          <ImageUploadPreviewCard
            imageHeight={IMAGE_HEIGHT_PX}
            imageWidth={IMAGE_WIDTH_PX}
            maxFileSize={MAX_FILE_SIZE_KB}
            allowedImageTypes={ALLOWED_IMAGE_TYPES}
            title="Current Badge"
            onRemove={handleRemoveImage}
            readonly={readonly}
            image={image[mode]}
            className={styles.uploadPreviewCard}
          />
        </Box>
      </Stack>
    </>
  );
};

export default PortalBadgeForm;
